/**
 * EventInfoCards — single overview card (merged from the former EventInfoCards + EventDetailsCard
 * three-box layout, #194). Fields flow through one responsive grid — label above
 * value, left-aligned — so the card fills its width instead of pinning labels
 * left and values right with a dead gap in the middle.
 *
 * All inputs autosave on blur — no Save buttons.
 */
import type { ReactNode } from "react";
import { Button } from "../ui/Button";
import { DateInputNZ } from "../ui/DateInputNZ";
import { SectionCard } from "../ui/SectionCard";
import { Select } from "../ui/Select";
import { Tooltip } from "../ui/Tooltip";
import { formatDateRange, getEventDayCount } from "../../lib/format-utils";
import { useEditableField } from "../../lib/use-editable-field";
import {
  checkField,
  emailRule,
  phoneRule,
  withinMaxLength,
} from "@shared/validators";
import type {
  HeaderField,
  WorkspaceEventData,
} from "../../lib/use-event-workspace";
import { InlineBanner } from "../ui/InlineBanner";
import { cx } from "@shared/cx";
import cell from "@/components/ui/cell-input.module.css";
import styles from "./EventInfoCards.module.css";

const EVENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "—" },
  { value: "wedding", label: "Wedding" },
  { value: "corporate", label: "Corporate" },
  { value: "festival", label: "Festival" },
  { value: "private_party", label: "Private Party" },
  { value: "other", label: "Other" },
];

/** How the Booking arrived — shown so an Operator reads a phone-sourced
 * Booking as more speculative than a submitted form. */
const INQUIRY_PATH_LABELS: Record<string, string> = {
  quick_enquiry: "Quick enquiry",
  full_brief: "Full brief",
  dry_hire: "Dry hire",
  phone_call: "Phone call",
};

const TEXT_MAX = 256;
const TEXTAREA_MAX = 1024;
const NOTES_MAX = 5000;
const tooLongMsg = (max: number) => `Keep this under ${max} characters`;
const lenRule = (max: number) => ({
  predicate: (v: string) => withinMaxLength(v, max),
  invalidMsg: tooLongMsg(max),
});
// email/phone validation (predicate + message) comes from the shared layer —
// imported as `emailRule` / `phoneRule` above — so this card can't drift from
// the inquiry step or the contractor/supplier modals.

interface Props {
  booking: WorkspaceEventData["booking"];
  /** Every document the customer uploaded through the inquiry form (#168). */
  inquiryDocuments: WorkspaceEventData["documents"]["inquiryDocuments"];
  /**
   * The inquiry's `additionalInfo` free-text — the client's own words. Shown
   * as a full-width field at the foot of the card when present. Rendered as
   * React text content (auto-escaped); never `dangerouslySetInnerHTML`.
   */
  additionalInfo?: string | null;
  savingHeaderFields: Set<string>;
  savingHeaderNotes?: boolean;
  onSaveField: (field: HeaderField, value: string | null) => void;
  onSaveHeaderNotes?: (value: string | null) => void;
}

function Field({
  label,
  children,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className={styles.fieldLabel}>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * One editable card field: label, autosave-on-blur input, paired role=alert
 * validation message. `useEditableField` owns the draft/focus-guard/validate
 * machine (the workspace autosave pipeline owns saving and save errors, via
 * `onSave` → the section's MutationRunner) — this component owns the markup
 * once, so a field can no longer drop the focus guard the way the hand-copied
 * Billing Email block did.
 */
function EditableTextField({
  label,
  ariaLabel,
  value,
  onSave,
  validate,
  saving,
  type = "text",
  placeholder,
}: {
  label: ReactNode;
  ariaLabel: string;
  value: string | null | undefined;
  onSave: (next: string | null) => void;
  validate: (trimmed: string) => string | undefined;
  saving: boolean;
  type?: string;
  placeholder?: string;
}) {
  const field = useEditableField(value, { validate, onSave });
  return (
    <Field label={label}>
      <input
        type={type}
        value={field.draft}
        onChange={(e) => field.setDraft(e.target.value)}
        onFocus={field.onFocus}
        onBlur={field.onBlur}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-invalid={Boolean(field.error) || undefined}
        disabled={saving}
        className={cx(cell.cellDense, field.error && cell.invalid)}
      />
      {field.error && (
        <InlineBanner variant="error">{field.error}</InlineBanner>
      )}
    </Field>
  );
}

export function EventInfoCards({
  booking,
  inquiryDocuments,
  additionalInfo,
  savingHeaderFields,
  savingHeaderNotes = false,
  onSaveField,
  onSaveHeaderNotes,
}: Props) {
  const dayCount = getEventDayCount(booking.event_date, booking.event_end_date);
  const multiDay =
    !!booking.event_end_date && booking.event_end_date !== booking.event_date;

  const headerNotes = useEditableField(booking.admin_header_notes, {
    validate: (v) => checkField(v, lenRule(NOTES_MAX)),
    onSave: (v) => onSaveHeaderNotes?.(v),
  });

  const isSaving = (f: HeaderField): boolean => savingHeaderFields.has(f);
  /** Props shared by every length-capped text field. */
  const textField = (field: HeaderField, max = TEXT_MAX) => ({
    validate: (v: string) => checkField(v, lenRule(max)),
    saving: isSaving(field),
    onSave: (v: string | null) => onSaveField(field, v),
  });

  // Collapsed-card one-liner: the fields an operator scans for at a glance.
  const summary =
    [
      booking.event_date
        ? formatDateRange(booking.event_date, booking.event_end_date, "short")
        : null,
      booking.location,
      booking.client_name,
    ]
      .filter(Boolean)
      .join("  ·  ") || "Event details";

  return (
    <SectionCard
      title="Event Details"
      collapsible
      defaultOpen={false}
      summary={summary}
      className={styles.card}
    >
      <div className={styles.notesBlock}>
        <label className={styles.notesLabel} htmlFor="admin-header-notes">
          Admin event notes
        </label>
        <textarea
          id="admin-header-notes"
          value={headerNotes.draft}
          onChange={(e) => headerNotes.setDraft(e.target.value)}
          onFocus={headerNotes.onFocus}
          onBlur={headerNotes.onBlur}
          aria-invalid={Boolean(headerNotes.error) || undefined}
          placeholder="Admin event notes"
          disabled={savingHeaderNotes}
          rows={2}
          className={cx(styles.notes, headerNotes.error && styles.notesInvalid)}
        />
        {headerNotes.error && (
          <InlineBanner variant="error">{headerNotes.error}</InlineBanner>
        )}
      </div>

      <dl className={styles.grid}>
        <Field label="Date">
          <div className={styles.dateRange}>
            <DateInputNZ
              value={booking.event_date ?? ""}
              onChange={(iso) => onSaveField("event_date", iso || null)}
              inputStyles={cell.cellDate}
              disabled={isSaving("event_date")}
              ariaLabel="Event date"
            />
            <span className={styles.dateArrow}>→</span>
            <DateInputNZ
              value={booking.event_end_date ?? ""}
              onChange={(iso) => onSaveField("event_end_date", iso || null)}
              inputStyles={cell.cellDate}
              disabled={isSaving("event_end_date")}
              ariaLabel="Event end date"
              min={booking.event_date || undefined}
            />
            {multiDay && <span className={styles.dayCount}>({dayCount}d)</span>}
          </div>
        </Field>

        <EditableTextField
          label="Time"
          ariaLabel="Event time"
          value={booking.event_time}
          {...textField("event_time")}
        />

        <EditableTextField
          label="Location"
          ariaLabel="Location"
          value={booking.location}
          {...textField("location")}
        />

        <EditableTextField
          label="Organization"
          ariaLabel="Organization"
          value={booking.organization}
          {...textField("organization")}
        />

        <EditableTextField
          label="Contact"
          ariaLabel="Client name"
          value={booking.client_name}
          {...textField("client_name")}
        />

        <EditableTextField
          label="Email"
          ariaLabel="Client email"
          type="email"
          value={booking.client_email}
          validate={(v) => checkField(v, emailRule)}
          saving={isSaving("client_email")}
          onSave={(v) => onSaveField("client_email", v)}
        />

        <EditableTextField
          label="Billing Email"
          ariaLabel="Billing email"
          type="email"
          placeholder="(same as Email)"
          value={booking.billing_email ?? ""}
          validate={(v) => checkField(v, emailRule)}
          saving={isSaving("billing_email")}
          onSave={(v) => onSaveField("billing_email", v)}
        />

        <EditableTextField
          label="Phone"
          ariaLabel="Client phone"
          type="tel"
          value={booking.client_phone}
          validate={(v) => checkField(v, phoneRule)}
          saving={isSaving("client_phone")}
          onSave={(v) => onSaveField("client_phone", v)}
        />

        <Field label="Type">
          <Select
            variant="cell"
            value={booking.event_type ?? ""}
            onChange={(next) => onSaveField("event_type", next || null)}
            ariaLabel="Event type"
            disabled={isSaving("event_type")}
            options={EVENT_TYPE_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
            }))}
          />
        </Field>

        {booking.inquiry_path && INQUIRY_PATH_LABELS[booking.inquiry_path] && (
          <Field label="Inquiry">
            <span className={styles.value}>
              {INQUIRY_PATH_LABELS[booking.inquiry_path]}
            </span>
          </Field>
        )}

        {booking.preferred_contact_method && (
          <Field label="Preferred Contact">
            <span className={styles.valueCapitalised}>
              {booking.preferred_contact_method}
            </span>
          </Field>
        )}

        {booking.attendance && (
          <Field label="Attendance">
            <span className={styles.value}>{booking.attendance}</span>
          </Field>
        )}

        {booking.indoor_outdoor && (
          <Field label="Environment">
            <span className={styles.value}>{booking.indoor_outdoor}</span>
          </Field>
        )}

        <EditableTextField
          label="Venue Contact"
          ariaLabel="Venue contact"
          value={booking.venue_contact}
          {...textField("venue_contact")}
        />

        {(booking.has_stage || booking.stage_details) && (
          <Field label="Stage">
            <span className={styles.value}>
              {booking.stage_details || "Yes"}
            </span>
          </Field>
        )}

        {booking.power_access && (
          <Field label="Power">
            <span className={styles.value}>{booking.power_access}</span>
          </Field>
        )}

        {booking.needs_generator && (
          <Field label="Generator">
            <span className={styles.value}>Required</span>
          </Field>
        )}

        <EditableTextField
          label="Wet Weather"
          ariaLabel="Wet weather plan"
          value={booking.wet_weather_plan}
          {...textField("wet_weather_plan", TEXTAREA_MAX)}
        />

        {/* PO number — editable header field (staff can set/correct it any
            time; the client otherwise only sets it at approval). Validated
            like the other text fields, and initialised from the loaded value
            so a blank re-save never clobbers a client-entered PO. */}
        <EditableTextField
          label={
            <Tooltip content="The Customer's purchase order reference, shown on their invoices.">
              PO Number
            </Tooltip>
          }
          ariaLabel="Purchase order number"
          value={booking.purchase_order}
          {...textField("purchase_order")}
        />

        {booking.content_requirements &&
          booking.content_requirements.length > 0 && (
            <Field label="Content">
              <span className={styles.value}>
                {booking.content_requirements.join(", ")}
              </span>
            </Field>
          )}

        {booking.request_summary &&
          booking.request_summary.trim().length > 0 && (
            <Field label="Request" className={styles.wide}>
              {/* The client's own words from the quick enquiry. */}
              <p className={styles.freeText}>{booking.request_summary}</p>
            </Field>
          )}

        {additionalInfo && additionalInfo.trim().length > 0 && (
          <Field label="Additional info" className={styles.wide}>
            {/* The client's own words. `white-space: pre-wrap` keeps their
                line breaks; `overflow-wrap` stops long tokens/URLs
                overflowing. Both live on `.freeText`. */}
            <p className={styles.freeText}>{additionalInfo}</p>
          </Field>
        )}
      </dl>

      {inquiryDocuments.length > 0 && (
        <div className={styles.documents}>
          <p className={styles.documentsLabel}>Uploaded documents</p>
          <div className={styles.documentList}>
            {inquiryDocuments.map((doc, i) => (
              <Button
                key={`${doc.url}-${i}`}
                variant="link"
                size="sm"
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {doc.name}
              </Button>
            ))}
          </div>
        </div>
      )}
    </SectionCard>
  );
}
